package com.veldekar.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.navigation.BasePath
import com.veldekar.components.metadata.updateOpenGraphMetadata
import kotlinx.browser.document
import kotlinx.browser.window

@Page
@Composable
fun MapPage() {
    // Inject minimal CSS to make the map truly fullscreen and bypass site layout paddings
    val existingElement = document.getElementById("map-fullscreen-style")
    if (existingElement == null) {
        val style = document.createElement("style")
        style.id = "map-fullscreen-style"
        // language=css
        style.textContent = """
            html, body { height: 100%; margin: 0; padding: 0; }
            #map { position: fixed; inset: 0; width: 100vw; height: 100vh; }
            .leaflet-control-close-btn a { 
                display: inline-block; 
                width: 28px; height: 28px; 
                line-height: 28px; 
                text-align: center; 
                font-weight: bold; 
                background: rgba(0,0,0,0.6); 
                color: white; 
                border-radius: 4px; 
                text-decoration: none; 
            }
            .leaflet-control-close-btn a:hover { background: rgba(0,0,0,0.8); }
            .leaflet-control-layers { color-scheme: light; }
        """.trimIndent()
        document.head?.appendChild(style)
    }

    org.jetbrains.compose.web.dom.Div(Modifier.id("map").toAttrs())

    LaunchedEffect(Unit) {
        document.title = "Veldekar - Map"
        updateOpenGraphMetadata(
            title = "Veldekar - Interactive World Map",
            description = "Explore the interactive hand-drawn world map of Veldekar, featuring regions, cities, waterways, and travel routes.",
            image = "images/veldekarMapPreview.webp",
            imageWidth = 1200,
            imageHeight = 630,
            imageAlt = "Hand-drawn fantasy world map of Veldekar"
        )
        loadLeafletAndInitializeMap()
    }
}

private fun loadLeafletAndInitializeMap() {
    // Check if Leaflet is already loaded
    if (js("typeof window.L !== 'undefined'") as Boolean) {
        initializeMap()
        return
    }

    // Load Leaflet CSS
    val leafletCssLink = document.createElement("link").unsafeCast<org.w3c.dom.HTMLLinkElement>()
    leafletCssLink.rel = "stylesheet"
    leafletCssLink.href = "https://unpkg.com/leaflet@1.9.3/dist/leaflet.css"
    leafletCssLink.setAttribute("integrity", "sha256-kLaT2GOSpHechhsozzB+flnD+zUyjE2LlfWPgU04xyI=")
    leafletCssLink.setAttribute("crossOrigin", "anonymous")
    document.head?.appendChild(leafletCssLink)

    // Load Leaflet JS from CDN
    val leafletScript = document.createElement("script").unsafeCast<org.w3c.dom.HTMLScriptElement>()
    leafletScript.src = "https://unpkg.com/leaflet@1.9.3/dist/leaflet.js"
    leafletScript.setAttribute("integrity", "sha256-WBkoXOwTeyKclOHuWtc+i2uENFpDZ9YPdf5Hf+D7ewM=")
    leafletScript.setAttribute("crossOrigin", "anonymous")

    leafletScript.onload = {
        // Load custom plugins after Leaflet is loaded
        loadMapPlugins()
    }

    document.head?.appendChild(leafletScript)
}

private fun loadMapPlugins() {
    // Load L.Control.MousePosition plugin
    val mousePositionScript = document.createElement("script").unsafeCast<org.w3c.dom.HTMLScriptElement>()
    mousePositionScript.src = BasePath.prependTo("/leaflet/L.Control.MousePosition.js")
    mousePositionScript.onload = {
        // Load L.Control.MousePosition CSS
        val mousePositionCss = document.createElement("link").unsafeCast<org.w3c.dom.HTMLLinkElement>()
        mousePositionCss.rel = "stylesheet"
        mousePositionCss.href = BasePath.prependTo("/leaflet/L.Control.MousePosition.css")
        document.head?.appendChild(mousePositionCss)

        // Load CountrySelect plugin
        loadCountrySelectPlugin()
    }
    document.head?.appendChild(mousePositionScript)
}

private fun loadCountrySelectPlugin() {
    val countrySelectScript = document.createElement("script").unsafeCast<org.w3c.dom.HTMLScriptElement>()
    countrySelectScript.src = BasePath.prependTo("/leaflet/Leaflet.CountrySelect.js")
    countrySelectScript.onload = {
        // All plugins loaded, now initialize the map
        window.setTimeout({
            initializeMap()
        }, 100)
    }
    document.head?.appendChild(countrySelectScript)
}

@Suppress("LongMethod")
private fun initializeMap() {
    val basePath = BasePath.value.dropLast(1) // Remove trailing slash for consistency
    // language=javascript
    val initScript = """
        var basePath = '$basePath';
        var CRSPixel = L.Util.extend(L.CRS.Simple, {
            transformation: new L.Transformation(1,0,1,0),
            distance(latlng1, latlng2) {
                const dx = latlng2.lng - latlng1.lng,
                    dy = latlng2.lat - latlng1.lat;
                return Math.sqrt(dx * dx + dy * dy);
            },
        });

        var map = new L.Map('map', {
            crs: CRSPixel,
            center: [2300, 1700],
            zoom: -1,
            minZoom: -3,
            maxZoom: 4,
            maxBounds: [[6,309],[4860,6799]]
        });

        var veldekarBounds = [[0,0],[4861,7200]];
        var veldekarTileLayer = new L.TileLayer(basePath + '/worldTiles/veldekar_z{z}_y{y}_x{x}.jpg', {
            tileSize: 1200,
            minZoom: -3,
            maxZoom: 4
        })
        veldekarTileLayer.addTo(map);

        var worldOutlineLayer = L.imageOverlay(basePath + '/Veldekar_outline.svg', veldekarBounds);
        var worldLandCoverLayer = L.imageOverlay(basePath + '/Veldekar_land_cover.svg', veldekarBounds);

        worldOutlineLayer.addTo(map);
        worldLandCoverLayer.addTo(map);

        var zoneTileLayer = new L.TileLayer(basePath + '/zoneTiles/veldekar_z{z}_y{y}_x{x}.webp', {
            tileSize: 1200,
            minZoom: 0,
            maxZoom: 3
        })

        function updateWorldVectorVisibility() {
            var zoom = map.getZoom();
            // Hide world vector layers if zoomed in enough that zone tiles are visible (zoom >= 0)
            var opacity = zoom < 0 ? 1 : 0;
            worldOutlineLayer.setOpacity(opacity);
            worldLandCoverLayer.setOpacity(opacity);
        }

        map.on('zoomend', updateWorldVectorVisibility);
        updateWorldVectorVisibility();

        var d5YbrallanBounds = [[3785,5341],[4026,5582]]
        var d5YbrallanImage = L.imageOverlay(basePath + '/localMaps/D5_Ybrallan_Area_crop.webp', d5YbrallanBounds)

        var zedjZoneMapBounds = [[3149,5092],[3398,5341]]
        var zedjZoneMapImage = L.imageOverlay(basePath + '/localMaps/Zedj_Area_Map_crop.webp', zedjZoneMapBounds)

        var vorsDesertZoneMapBounds = [[2864,5695],[3113,5944]]
        var vorsDesertZoneMapImage = L.imageOverlay(basePath + '/localMaps/Southwestern_Vors_Desert.webp', vorsDesertZoneMapBounds)

        var seshissZoneMapBounds = [[1078,3714],[1302,3938]]
        var seshissZoneMapImage = L.imageOverlay(basePath + '/localMaps/Seshiss_Area_Map.webp', seshissZoneMapBounds)

        var freesburgBounds = [[1662.96, 1581.02], [1728.91, 1647.56]]
        fetch(basePath + '/localMaps/Freeburg.svg')
            .then(function(r) { return r.text(); })
            .then(function(svgText) {
                var parser = new DOMParser();
                var svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
                var svgEl = svgDoc.documentElement;
                var freesburgOverlay = L.svgOverlay(svgEl, freesburgBounds, { interactive: true });

                var freesburgTooltip = document.createElement('div');
                freesburgTooltip.style.cssText = 'position:fixed;background:rgba(0,0,0,0.75);color:white;padding:4px 8px;border-radius:4px;font-size:13px;pointer-events:none;display:none;z-index:9999;white-space:nowrap;';
                document.body.appendChild(freesburgTooltip);

                svgEl.querySelectorAll('g > title').forEach(function(titleEl) {
                    var g = titleEl.parentElement;
                    var text = titleEl.textContent.trim();
                    titleEl.remove();
                    g.style.pointerEvents = 'all';
                    g.style.cursor = 'help';
                    g.addEventListener('mouseover', function(e) {
                        freesburgTooltip.textContent = text;
                        freesburgTooltip.style.display = 'block';
                        freesburgTooltip.style.left = (e.clientX + 14) + 'px';
                        freesburgTooltip.style.top  = (e.clientY - 30) + 'px';
                        e.stopPropagation();
                    });
                    g.addEventListener('mousemove', function(e) {
                        freesburgTooltip.style.left = (e.clientX + 14) + 'px';
                        freesburgTooltip.style.top  = (e.clientY - 30) + 'px';
                    });
                    g.addEventListener('mouseout', function() {
                        freesburgTooltip.style.display = 'none';
                    });
                });

                function updateFreesburgOpacity() {
                    freesburgOverlay.setOpacity(map.getZoom() >= 3 ? 1 : 0);
                }
                map.on('zoomend', updateFreesburgOpacity);
                map.on('layeradd', function(e) {
                    if (e.layer === freesburgOverlay) { updateFreesburgOpacity(); }
                });
                freesburgOverlay.addTo(map);
                updateFreesburgOpacity();
                layerControl.addOverlay(freesburgOverlay, 'Freeburg');
            });

        var baseLayers = {};

        var nineth = [3917,5398]
        var tenth = [3888,5416]
        var eleventhCultists = [3873,5426]
        var eleventhNightCamp = [3857,5435]
        var ninethMarker = L.marker(nineth).bindPopup("9th of Newgrowth Campsite", {autoClose:false});
        var tenthMarker = L.marker(tenth).bindPopup("10th of Newgrowth Campsite", {autoClose:false});
        var eleventhMarker = L.marker(eleventhCultists).bindPopup("11th of Newgrowth Cultists", {autoClose:false});
        var eleventhNightCampMarker = L.marker(eleventhNightCamp).bindPopup("11th of Newgrowth Campsite", {autoClose:false});

        var brothersCsiPolyline = L.polyline([[3921,5386],nineth, tenth, eleventhCultists, eleventhNightCamp], {color: 'pink'});
        var brothersCsiLayerGroup = L.layerGroup([ninethMarker, tenthMarker, eleventhMarker, brothersCsiPolyline, eleventhNightCampMarker]);

        var ybrallan = [3921,5386]
        var part2_ybrallanMarker = L.marker(ybrallan).bindPopup("Leering Stag / Damon", {autoClose:false});
        var part2_17_330pm = [3920.22411, 5398.60507]
        var part2_17_630pm = [3920.34904, 5406.22727]
        var part2_18_12pm = [3920.34904, 5421.34672]
        var part2_endPoint = L.marker(part2_18_12pm).bindPopup("18th Newgrowth 12pm", {autoClose:false});
        var part2_travelLine = L.polyline([ybrallan,part2_17_330pm, part2_17_630pm, part2_18_12pm], {color: 'pink'});

        var part2_dorb = [3808.99283, 5535.67153]
        var part2_dorvidianEmpire = [3769.75977, 1491.93731]
        var part2_zedj = [3395.95684, 5114.85557]
        var part2_janizavi = [2654.93314, 5088.43786]
        var part2_pergoma = [227.89302, 5262.90895]
        var part2_layerGroup = L.layerGroup([
            part2_travelLine,
            L.marker(part2_dorb).bindPopup("Sir Solon Dragstone (a high level officer in the Malusian Calvary) and Sir Lorn Fogarth of the Peers", {autoClose:false}),
            L.marker(part2_dorvidianEmpire).bindPopup("(rumor) Dorvidian Empire has put a contract on our heroes's heads", {autoClose:false}),
            L.marker(part2_zedj).bindPopup("Damon wants heroes to investigate here", {autoClose:false}),
            L.marker(part2_janizavi).bindPopup("Home of Mardeth Kromnigard, a wizard of the peers dispatched to Zedj to investigate. Mardeth was an up-and-coming member of the guild but we haven't heard about him recently.", {autoClose:false}),
            L.marker(part2_pergoma).bindPopup("Island where the dimensional portal is", {autoClose:false}),
            part2_ybrallanMarker,
            part2_endPoint
        ]);

        var overlays = {
            "World Outline": worldOutlineLayer,
            "World Land Cover": worldLandCoverLayer,
            "Original Tiles": veldekarTileLayer,
            "Zone" : zoneTileLayer,
            "Seshiss" : seshissZoneMapImage,
            "Vors Desert" : vorsDesertZoneMapImage,
            "Zedj" : zedjZoneMapImage,
            "D5 Ybrallan" : d5YbrallanImage,
            "Brothers CSI" : brothersCsiLayerGroup,
            "Campaign Part 2" : part2_layerGroup
        }
        zoneTileLayer.addTo(map)

        var select = L.countrySelect({});
        select.addTo(map);

        select.on('change', function(e){
            if (e.feature === undefined){
                if (this.previousCountry != null){
                    map.removeLayer(this.previousCountry);
                }
                return;
            }
            var country = L.geoJson(e.feature);
            if (this.previousCountry != null){
                map.removeLayer(this.previousCountry);
            }
            this.previousCountry = country;

            map.addLayer(country);
            map.fitBounds(country.getBounds(), {maxZoom:0});
        });

        var layerControl = L.control.layers(baseLayers, overlays, {position: 'topleft'}).addTo(map)

        // Add a Leaflet close button control to navigate back to the main site
        L.Control.CloseButton = L.Control.extend({
            options: { position: 'topright' },
            onAdd: function(map) {
                var container = L.DomUtil.create('div', 'leaflet-control-close-btn leaflet-bar');
                var link = L.DomUtil.create('a', '', container);
                link.innerHTML = '✕';
                link.href = '#';
                link.setAttribute('aria-label', 'Close and return to home');
                L.DomEvent.disableClickPropagation(container);
                L.DomEvent.on(link, 'click', function(e) {
                    L.DomEvent.stop(e);
                    window.location.href = basePath + '/';
                });
                return container;
            }
        });
        L.control.closeButton = function(opts) { return new L.Control.CloseButton(opts); }
        L.control.closeButton({ position: 'topright' }).addTo(map);

        // Create custom dropdown styling with flag icons
        const style = document.createElement('style');
        style.textContent = `
            .country-select-container {
                position: relative;
                display: inline-block;
                background: white;
                border: 1px solid #999;
                border-radius: 4px;
                cursor: pointer;
                color: #333;
            }

            .country-select-button {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 8px 12px;
                background: white;
                border: none;
                cursor: pointer;
                font-size: 14px;
                min-width: 150px;
                color: #333;
            }
            
            .country-select-button:hover {
                background-color: #f0f0f0;
            }
            
            .country-flag {
                width: 26px; /* slightly wider to avoid clipping */
                height: 18px;
                background-size: contain; /* prevent cropping */
                background-repeat: no-repeat;
                background-position: left center;
                border-radius: 2px;
            }
            
            .input-field.leaflet-control {
                z-index: 900 !important;
            }

            .country-select-dropdown {
                position: absolute;
                top: 100%;
                left: 0;
                background: white;
                border: 1px solid #999;
                border-top: none;
                border-radius: 0 0 4px 4px;
                max-height: max(300px, calc(100dvh - 65px));
                overflow-y: auto;
                width: 100%;
                display: none;
                z-index: 1000;
                box-shadow: 0 4px 6px rgba(0,0,0,0.1);
                color: #333;
                color-scheme: light;
            }
            
            .country-select-dropdown.show {
                display: block;
            }
            
            .country-option {
                display: flex;
                align-items: center;
                gap: 10px;
                padding: 10px 12px;
                cursor: pointer;
                border-bottom: 1px solid #eee;
            }
            
            .country-option:last-child {
                border-bottom: none;
            }
            
            .country-option:hover {
                background-color: #f9f9f9;
            }
            
            .country-option.selected {
                background-color: #e3f2fd;
            }
            
            .country-option-flag {
                width: 26px; /* slightly wider to avoid clipping */
                height: 18px;
                background-size: contain; /* prevent cropping */
                background-repeat: no-repeat;
                background-position: left center;
                border-radius: 2px;
                flex-shrink: 0;
            }
            
            .country-option-name {
                flex: 1;
                font-size: 14px;
                white-space: nowrap;
            }
        `;
        document.head.appendChild(style);
        
        // Find the country select and replace it with custom dropdown
        const countrySelect = document.getElementById('country-select');
        if (countrySelect) {
            const container = document.createElement('div');
            container.className = 'country-select-container';
            
            const button = document.createElement('div');
            button.className = 'country-select-button';
            
            const flagImg = document.createElement('div');
            flagImg.className = 'country-flag';
            button.appendChild(flagImg);
            
            const nameSpan = document.createElement('span');
            nameSpan.textContent = 'Country';
            button.appendChild(nameSpan);
            
            const dropdown = document.createElement('div');
            dropdown.className = 'country-select-dropdown';
            
            // Hide the original Leaflet label element
            const originalLabel = countrySelect.parentNode.querySelector('label');
            if (originalLabel) {
                originalLabel.style.display = 'none';
            }
            
            // Calculate dropdown width based on longest country name
            let maxWidth = 160; // start a bit larger
            Array.from(countrySelect.options).forEach(function(option) {
                if (option.value) {
                    const tempSpan = document.createElement('span');
                    tempSpan.textContent = option.text;
                    tempSpan.style.visibility = 'hidden';
                    tempSpan.style.position = 'absolute';
                    tempSpan.style.fontSize = '14px';
                    tempSpan.style.whiteSpace = 'nowrap';
                    document.body.appendChild(tempSpan);
                    // Add extra space for flag + gaps + padding
                    maxWidth = Math.max(maxWidth, tempSpan.offsetWidth + 80);
                    tempSpan.remove();
                }
            });
            
            // Populate dropdown from select options
            Array.from(countrySelect.options).forEach(function(option, index) {
                if (index === 0) return; // Skip empty option
                
                const optionDiv = document.createElement('div');
                optionDiv.className = 'country-option';
                optionDiv.dataset.value = option.value;
                
                if (option.dataset.icon) {
                    const flagDiv = document.createElement('div');
                    flagDiv.className = 'country-option-flag';
                    flagDiv.style.backgroundImage = 'url(' + option.dataset.icon + ')';
                    optionDiv.appendChild(flagDiv);
                }
                
                const nameDiv = document.createElement('div');
                nameDiv.className = 'country-option-name';
                nameDiv.textContent = option.text;
                optionDiv.appendChild(nameDiv);
                
                optionDiv.addEventListener('click', function() {
                    countrySelect.value = option.value;
                    const event = new Event('change', {bubbles: true});
                    countrySelect.dispatchEvent(event);
                    
                    nameSpan.textContent = option.text;
                    if (option.dataset.icon) {
                        flagImg.style.backgroundImage = 'url(' + option.dataset.icon + ')';
                        flagImg.style.backgroundSize = 'contain';
                        flagImg.style.backgroundRepeat = 'no-repeat';
                        flagImg.style.backgroundPosition = 'left center';
                    }
                    dropdown.classList.remove('show');
                });
                
                dropdown.appendChild(optionDiv);
            });
            
            // Set dropdown width to match button and content
            dropdown.style.width = maxWidth + 'px';
            container.style.width = maxWidth + 'px';
            
            button.addEventListener('click', function() {
                dropdown.classList.toggle('show');
            });
            
            // Close dropdown when clicking outside
            document.addEventListener('click', function(e) {
                if (!container.contains(e.target)) {
                    dropdown.classList.remove('show');
                }
            });
            
            container.appendChild(button);
            container.appendChild(dropdown);
            // Stop wheel events from reaching the Leaflet map (Leaflet's built-in
            // disableScrollPropagation only blocks legacy mousewheel events, not wheel)
            container.addEventListener('wheel', function(e) { e.stopPropagation(); }, false);
            countrySelect.style.display = 'none';
            countrySelect.parentNode.insertBefore(container, countrySelect);
        }
    """.trimIndent()

    val script = document.createElement("script")
    script.textContent = initScript
    document.body?.appendChild(script)
    script.remove()
}
